import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { useMemo, useEffect, useState } from "react";

import type { RootState } from "../../store";
import { useScholarships } from "../../services/scholarshipService";

interface DisplayScholarship {
  id: string | number;
  title: string;
  description: string;
  amount?: string;
  deadline?: string;
  type?: string;
  status: string;
  donorName?: string;
  donorPicture?: string;
}

interface ScholarshipsSectionProps {
  title?: string;
  subtitle?: string;
  scholarships?: DisplayScholarship[];
  showViewAll?: boolean;
  onViewAll?: () => void;
  onApply?: (scholarship: DisplayScholarship) => void;
}

// Helper function to strip HTML tags for preview
const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const ScholarshipsSection: React.FC<ScholarshipsSectionProps> = ({
  title,
  subtitle,
  scholarships: propScholarships,
  showViewAll = true,
  onViewAll,
  onApply,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const [isLoaded, setIsLoaded] = useState(false);

  // Fetch scholarships from API
  const { data: scholarshipsResponse, isLoading } = useScholarships({
    locale: currentLanguage,
    per_page: 3,
    // status: "open",
  });

  // Map API scholarships to DisplayScholarship format
  const apiScholarships: DisplayScholarship[] = useMemo(() => {
    if (!scholarshipsResponse?.scholarships) return [];

    return scholarshipsResponse.scholarships.map((s) => {
      const activeSession = s.sessions?.find(sess => sess.is_active) || s.sessions?.[0];
      const mainSponsor = activeSession?.sponsors?.find(sp => sp.is_main) || activeSession?.sponsors?.[0];

      return {
        id: s.id,
        title: s.translations?.find(tr => tr.locale === currentLanguage)?.title || s.translations?.[0]?.title || "",
        description: s.translations?.find(tr => tr.locale === currentLanguage)?.description || s.translations?.[0]?.description || "",
        amount: s.translations?.find(tr => tr.locale === currentLanguage)?.offers || s.translations?.[0]?.offers || "",
        deadline: activeSession?.academic_year, // Using academic year as a placeholder for deadline/context
        status: s.status,
        donorName: mainSponsor?.name,
        donorPicture: mainSponsor?.picture
      };
    });
  }, [scholarshipsResponse, currentLanguage]);

  // Use props if provided, otherwise use API data, otherwise use defaults
  const displayScholarships = propScholarships || apiScholarships;

  useEffect(() => {
    if (!isLoading) {
      setIsLoaded(true);
    }
  }, [isLoading]);

  return (
    <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-white via-emerald-50/50 to-stone-100/50 dark:from-gray-800 dark:via-emerald-900/10 dark:to-stone-900/10">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={
              isLoaded ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-emerald-800 dark:text-emerald-300 rounded-full text-sm font-semibold border border-emerald-200 dark:border-emerald-800 shadow-lg">
              <span className="text-lg">🎓</span>
              <span>{t("scholarships.badge", "Education First")}</span>
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight"
          >
            {title || t("scholarships.title", "Scholarship Programs")}
            <span className="block mt-2 bg-gradient-to-r from-emerald-600 via-stone-500 to-emerald-700 bg-clip-text text-transparent">
              {t("scholarships.titleAccent", "Invest in Your Future")}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto"
          >
            {subtitle ||
              t(
                "scholarships.subtitle",
                "We believe in the power of education. Discover opportunities designed to support your academic journey."
              )}
          </motion.p>
        </motion.div>

        {/* Scholarships Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10">
          {displayScholarships.map((scholarship, index) => (
            <motion.div
              key={scholarship.id}
              initial={{ opacity: 0, y: 50 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              whileHover={{ y: -12, scale: 1.03 }}
              className="group relative"
            >
              {/* Floating gradient glow */}
              <div
                className={`absolute -inset-1 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500 ${scholarship.status === "closing_soon"
                  ? "bg-gradient-to-r from-stone-500 to-stone-600"
                  : "bg-gradient-to-r from-emerald-500 to-stone-500"
                  }`}
              ></div>

              {/* Main Card */}
              <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 h-full flex flex-col">
                {/* Header Section */}
                <div className="flex justify-between items-start mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 mb-4">
                    <svg
                      className="w-8 h-8"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
                      />
                    </svg>
                  </div>
                  {scholarship.status === "closing_soon" && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-stone-100 text-stone-800 dark:bg-stone-900/50 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                      Closing Soon
                    </span>
                  )}
                </div>

                {/* Content */}
                <h3 className="text-xl font-extrabold text-gray-900 dark:text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-stone-600 transition-all duration-300">
                  {scholarship.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-6 flex-1 line-clamp-3">
                  {stripHtml(scholarship.description)}
                </p>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {/* <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t("scholarships.amount", "Amount")}
                    </div>
                    <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                      {scholarship.amount}
                    </div>
                  </div> */}
                  <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-3">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t("scholarships.deadline", "Deadline")}
                    </div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                      {scholarship.deadline
                        ? new Date(scholarship.deadline).toLocaleDateString()
                        : t("scholarships.noDeadline", "N/A")}
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    if (onApply) {
                      onApply(scholarship);
                    } else {
                      navigate(`/${currentLanguage}/scholarships/${scholarship.id}`);
                    }
                  }}
                  className={`w-full px-4 py-3 bg-gradient-to-r ${scholarship.status === "closing_soon"
                    ? "from-stone-500 to-stone-600 hover:from-stone-600 hover:to-stone-700"
                    : "from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700"
                    } text-white rounded-xl font-bold transition-all duration-200 text-sm shadow-lg hover:shadow-xl mt-auto`}
                >
                  {t("scholarships.apply", "Apply Now")} →
                </motion.button>

                {/* Decorative gradient corner */}
                <div
                  className={`absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br ${scholarship.status === "closing_soon"
                    ? "from-stone-500 to-stone-600"
                    : "from-emerald-500 to-stone-600"
                    } opacity-10 group-hover:opacity-20 rounded-full blur-2xl transition-opacity duration-500`}
                ></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        {showViewAll && displayScholarships.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mt-20"
          >
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (onViewAll) {
                  onViewAll();
                } else {
                  navigate(`/${currentLanguage}/scholarships`);
                }
              }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 text-gray-900 dark:text-white rounded-2xl font-bold text-lg transition-all duration-200 shadow-xl hover:shadow-2xl border-2 border-gray-200 dark:border-gray-700"
            >
              {t("scholarships.viewAll", "View All Scholarships")}
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ScholarshipsSection;
